import { useRef, useState } from "react";
import { toast } from "sonner";
import { FileSearch, Loader2, Paperclip, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { useI18n } from "@/lib/i18n";
import { useCreerDemandeVerification, useUploadPieceJointeDemande } from "@/hooks/useDemandeVerification";
import type { DemandeVerificationAttachmentDto } from "@/types/demande-verification.types";

/** Bouton + formulaire public : soumettre un contenu à vérifier, sans compte,
 * avec pièces jointes optionnelles (image/audio/vidéo/document) et — si
 * souhaité — des coordonnées pour être recontacté. */
export function DemandeVerificationDialog() {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [texte, setTexte] = useState("");
  const [pieces, setPieces] = useState<DemandeVerificationAttachmentDto[]>([]);
  const [souhaiteRecontact, setSouhaiteRecontact] = useState(false);
  const [nom, setNom] = useState("");
  const [email, setEmail] = useState("");
  const [telephone, setTelephone] = useState("");
  const [erreur, setErreur] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const creerDemande = useCreerDemandeVerification();
  const uploadPieceJointe = useUploadPieceJointeDemande();

  const reinitialiser = () => {
    setTexte("");
    setPieces([]);
    setSouhaiteRecontact(false);
    setNom("");
    setEmail("");
    setTelephone("");
    setErreur("");
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const piece = await uploadPieceJointe.mutateAsync(file);
      setPieces((prev) => [...prev, piece]);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t("verificationRequest.uploadError"));
    }
  };

  const retirerPiece = (s3Key: string) => {
    setPieces((prev) => prev.filter((p) => p.s3Key !== s3Key));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!texte.trim()) {
      setErreur(t("verificationRequest.textRequired"));
      return;
    }
    if (souhaiteRecontact && !nom.trim() && !email.trim() && !telephone.trim()) {
      setErreur(t("verificationRequest.contactRequired"));
      return;
    }
    setErreur("");
    try {
      await creerDemande.mutateAsync({
        texte: texte.trim(),
        nom: nom.trim() || undefined,
        email: email.trim() || undefined,
        telephone: telephone.trim() || undefined,
        souhaiteRecontact,
        pieces,
      });
      toast.success(t("verificationRequest.success"));
      setOpen(false);
      reinitialiser();
    } catch (err: any) {
      setErreur(err?.response?.data?.message || t("verificationRequest.submitError"));
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1 text-xs font-medium text-foreground hover:bg-surface transition-colors"
      >
        <FileSearch className="h-3.5 w-3.5" />
        <span className="hidden sm:inline">{t("verificationRequest.cta")}</span>
      </button>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reinitialiser(); }}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("verificationRequest.title")}</DialogTitle>
            <DialogDescription>{t("verificationRequest.description")}</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {erreur && (
              <div className="rounded-md bg-alert/10 border border-alert/30 px-3 py-2 text-xs text-alert font-medium">
                {erreur}
              </div>
            )}

            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {t("verificationRequest.textLabel")}
              </label>
              <textarea
                value={texte}
                onChange={(e) => setTexte(e.target.value)}
                rows={5}
                placeholder={t("verificationRequest.textPlaceholder")}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-foreground"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {t("verificationRequest.attachments")}
              </label>
              <div className="flex flex-wrap gap-2">
                {pieces.map((p) => (
                  <span
                    key={p.s3Key}
                    className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-2 py-1 text-xs"
                  >
                    <Paperclip className="h-3 w-3" /> {p.type}
                    <button type="button" onClick={() => retirerPiece(p.s3Key)} aria-label="Retirer">
                      <X className="h-3 w-3 text-muted-foreground hover:text-alert" />
                    </button>
                  </span>
                ))}
              </div>
              <label className="flex w-fit cursor-pointer items-center gap-2 rounded-md border border-dashed border-border bg-surface/40 px-3 py-2 text-xs text-muted-foreground hover:border-foreground hover:text-foreground hover:bg-surface transition-colors">
                {uploadPieceJointe.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />}
                {t("verificationRequest.addAttachment")}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,video/*,audio/*"
                  className="hidden"
                  onChange={handleFileChange}
                  disabled={uploadPieceJointe.isPending}
                />
              </label>
              <p className="text-[11px] text-muted-foreground">{t("verificationRequest.attachmentHint")}</p>
            </div>

            <label className="flex items-center gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                checked={souhaiteRecontact}
                onChange={(e) => setSouhaiteRecontact(e.target.checked)}
              />
              {t("verificationRequest.wantsContact")}
            </label>

            {souhaiteRecontact && (
              <div className="grid gap-3 sm:grid-cols-2 rounded-md border border-border bg-surface/40 p-3">
                <div className="space-y-1.5 sm:col-span-2">
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {t("verificationRequest.name")}
                  </label>
                  <input
                    value={nom}
                    onChange={(e) => setNom(e.target.value)}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {t("verificationRequest.email")}
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {t("verificationRequest.phone")}
                  </label>
                  <input
                    value={telephone}
                    onChange={(e) => setTelephone(e.target.value)}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  />
                </div>
              </div>
            )}

            <DialogFooter>
              <button
                type="submit"
                disabled={creerDemande.isPending}
                className="inline-flex items-center gap-2 rounded-md bg-foreground px-4 py-2 text-sm font-semibold text-background disabled:opacity-60"
              >
                {creerDemande.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                {t("verificationRequest.submit")}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
